import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { reservationService } from '../services/reservationService'
import type { ConflictCheck } from '../services/reservationService'

// CRITICAL FIX: Use sessionStorage for tab-specific session ID
// This ensures two tabs from the same user can reserve different numbers independently
// sessionStorage is isolated per browser tab, unlike localStorage which is shared
const getOrCreateTabSessionId = (): string => {
  const SESSION_KEY = 'cart-tab-session-id'
  let tabId = sessionStorage.getItem(SESSION_KEY)
  if (!tabId) {
    tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    sessionStorage.setItem(SESSION_KEY, tabId)
  }
  return tabId
}

interface CartItem {
  raffleId: string
  number: number
  basePrice: number
  upgrades?: {
    golden?: boolean
    boost?: boolean
    hot?: boolean
  }
}

interface CartReservation {
  raffleId: string
  numbers: number[]
  sessionId: string
  expiresAt: number // timestamp
  createdAt: number
}

interface CartState {
  items: CartItem[]
  reservation: CartReservation | null
  timeRemaining: number
  isExpired: boolean
  isProcessingPayment: boolean
  sessionId: string // Persistent session ID for this user
}

interface CartActions {
  addItem: (item: CartItem) => void
  removeItem: (raffleId: string, number: number) => void
  clearCart: () => void
  createReservation: (raffleId: string, numbers: number[], userId: string) => Promise<{ success: boolean, conflicts?: ConflictCheck }>
  extendReservation: (minutes: number) => Promise<void>
  clearReservation: () => Promise<void>
  updateTimeRemaining: () => void
  applyUpgrade: (number: number, upgradeType: 'golden' | 'boost' | 'hot') => void
  removeUpgrade: (number: number, upgradeType: 'golden' | 'boost' | 'hot') => void
  setProcessingPayment: (processing: boolean) => void
  calculateTotal: () => number
  calculateUpgradeCost: () => number
  confirmPurchase: (userId: string, purchasePrice: number) => Promise<void>
  generateNewSessionId: () => void // Add method to regenerate sessionId if needed
}

export type CartStore = CartState & CartActions

export const useCartStore = create<CartStore>()(
  devtools(
    persist(
      (set, get) => ({
        // State
        items: [],
        reservation: null,
        timeRemaining: 0,
        isExpired: false,
        isProcessingPayment: false,
        // CRITICAL FIX: Use tab-specific sessionId from sessionStorage
        // This ensures two tabs from the same user can reserve different numbers
        sessionId: getOrCreateTabSessionId(),

        // Actions
        addItem: (item) => {
          set((state) => ({
            items: [...state.items.filter(i => !(i.raffleId === item.raffleId && i.number === item.number)), item]
          }))
        },

        removeItem: (raffleId, number) => {
          set((state) => ({
            items: state.items.filter(i => !(i.raffleId === raffleId && i.number === number))
          }))
        },

        clearCart: () => {
          set((state) => ({
            items: [],
            reservation: null,
            timeRemaining: 0,
            isExpired: false,
            isProcessingPayment: false,
            sessionId: state.sessionId // CRITICAL FIX: Keep sessionId persistent across cart clears
          }))
        },

        createReservation: async (
          raffleId: string,
          numbers: number[],
          userId: string
        ): Promise<{ success: boolean; conflicts?: ConflictCheck }> => {
          try {
            if (!userId) {
              throw new Error('User ID is required to reserve tickets')
            }

            // CRITICAL FIX: Always use tab-specific sessionId from sessionStorage
            // This ensures two tabs from the same user can reserve different numbers
            const tabSessionId = getOrCreateTabSessionId()
            // Combine tab session with userId for full traceability
            const effectiveSessionId = `session_${userId}_${tabSessionId}`
            set({ sessionId: effectiveSessionId })

            // CRITICAL FIX: Use persistent sessionId and check conflicts with current session context
            const conflictCheck = await reservationService.checkForConflicts(raffleId, numbers, effectiveSessionId)

            if (conflictCheck.hasConflicts) {
              console.warn('⚠️ Conflicts detected:', conflictCheck.conflictingNumbers)
              return { 
                success: false, 
                conflicts: conflictCheck 
              }
            }

            const now = Date.now()
            const expiresAt = now + (5 * 60 * 1000) // 5 minutes
            const sessionId = effectiveSessionId

          // Create reservation in Firebase
          await reservationService.reserveNumbers({
            raffleId,
            numbers,
            sessionId,
            reservationOwnerId: userId,
            expiresAt: new Date(expiresAt)
          })

            set({
              reservation: {
                raffleId,
                numbers,
                sessionId,
                expiresAt,
                createdAt: now
              },
              timeRemaining: 5 * 60, // 5 minutes in seconds
              isExpired: false
            })

            // CRITICAL FIX: Remove the timer completely from store to prevent Square.js interference
            // Timer logic moved to external hook to avoid any store updates that cause re-renders
            // Only check expiration without updating timeRemaining in store
            const timer: ReturnType<typeof setInterval> = setInterval(() => {
              const state = get()
              if (!state.reservation) {
                clearInterval(timer)
                return
              }

              const now = Date.now()
              const remaining = Math.max(0, Math.floor((state.reservation.expiresAt - now) / 1000))

              // Only update store when timer expires - no intermediate updates
              if (remaining <= 0) {
                // FIXED: Don't clear reservation if payment is in progress
                // This prevents losing the reservation during the payment API call
                if (state.isProcessingPayment) {
                  // Payment in flight - extend reservation silently and continue
                  // The payment will complete or fail, don't interrupt it
                  return
                }
                clearInterval(timer)
                get().clearReservation()
                set({ isExpired: true })
              }
              // REMOVED: All intermediate timeRemaining updates to prevent re-renders
            }, 1000)

            return { success: true }
          } catch (error) {
            console.error('Error creating reservation:', error)
            return { 
              success: false, 
              conflicts: { 
                hasConflicts: true, 
                conflictingNumbers: numbers, 
                availableNumbers: [] 
              } 
            }
          }
        },

        extendReservation: async (minutes) => {
          const state = get()
          if (!state.reservation) return

          try {
            await reservationService.extendReservation(state.reservation.sessionId, minutes)
            
            const newExpiresAt = state.reservation.expiresAt + (minutes * 60 * 1000)
            set({
              reservation: {
                ...state.reservation,
                expiresAt: newExpiresAt
              },
              timeRemaining: state.timeRemaining + (minutes * 60)
            })
          } catch (error) {
            console.error('Error extending reservation:', error)
          }
        },

        clearReservation: async () => {
          const state = get()
          if (state.reservation) {
            try {
              await reservationService.releaseReservation(state.reservation.sessionId)
            } catch (error) {
              console.error('Error releasing reservation:', error)
            }
          }

          set({
            reservation: null,
            timeRemaining: 0,
            isExpired: false,
            items: []
          })
        },

        updateTimeRemaining: () => {
          const state = get()
          if (!state.reservation) return

          const now = Date.now()
          const remaining = Math.max(0, Math.floor((state.reservation.expiresAt - now) / 1000))

          // CRITICAL FIX: Only update store on expiration, never for time updates
          if (remaining <= 0) {
            // FIXED: Don't clear reservation if payment is in progress
            if (state.isProcessingPayment) {
              return // Payment in flight, don't interrupt
            }
            get().clearReservation()
            set({ isExpired: true })
          }
          // REMOVED: All timeRemaining updates to prevent Square.js re-renders
        },

        applyUpgrade: (number, upgradeType) => {
          set((state) => ({
            items: state.items.map(item => 
              item.number === number 
                ? {
                    ...item,
                    upgrades: {
                      ...item.upgrades,
                      [upgradeType]: true
                    }
                  }
                : item
            )
          }))
        },

        removeUpgrade: (number, upgradeType) => {
          set((state) => ({
            items: state.items.map(item => 
              item.number === number 
                ? {
                    ...item,
                    upgrades: {
                      ...item.upgrades,
                      [upgradeType]: false
                    }
                  }
                : item
            )
          }))
        },

        setProcessingPayment: (processing) => {
          set({ isProcessingPayment: processing })
        },

        calculateTotal: () => {
          const state = get()
          // Base price for all items
          const baseTotal = state.items.reduce((total, item) => total + item.basePrice, 0)

          // Add upgrade costs using the same logic as calculateUpgradeCost
          const upgradeTotal = get().calculateUpgradeCost()


          return baseTotal + upgradeTotal
        },

        calculateUpgradeCost: () => {
          const state = get()

          // Count golden numbers
          const goldenCount = state.items.filter(item => item.upgrades?.golden).length

          // Special pricing for golden numbers when 8+ total items
          let goldenCost = 0
          if (goldenCount > 0) {
            if (state.items.length >= 8) {
              if (goldenCount === 1) {
                // 8+ numbers with exactly 1 golden: FREE (promoción 1 dorado gratis)
                goldenCost = 0
              } else {
                // 8+ numbers with 2+ golden: €3 total for ALL golden numbers
                goldenCost = 3
              }
            } else {
              // Less than 8 numbers: €3 per golden number
              goldenCost = goldenCount * 3
            }
          }

          // Calculate other upgrades normally
          const otherUpgradeCost = state.items.reduce((total, item) => {
            let upgradeTotal = 0

            if (item.upgrades?.boost) upgradeTotal += 3
            if (item.upgrades?.hot) upgradeTotal += 2

            return total + upgradeTotal
          }, 0)


          return goldenCost + otherUpgradeCost
        },

        confirmPurchase: async (userId: string, purchasePrice: number) => {
          const state = get()
          if (!state.reservation) {
            throw new Error('No hay reserva activa para confirmar')
          }

          try {
            await reservationService.confirmPurchase(state.reservation.sessionId, userId, purchasePrice)
            
            set({
              reservation: null,
              timeRemaining: 0,
              isExpired: false,
              items: [],
              isProcessingPayment: false
            })
          } catch (error) {
            console.error('Error confirming purchase:', error)
            throw error
          }
        },

        generateNewSessionId: () => {
          set({
            sessionId: reservationService.generateSessionId()
          })
        }
      }),
      {
        name: 'cart-storage',
        partialize: (state) => ({
          items: state.items,
          reservation: state.reservation,
          timeRemaining: state.timeRemaining
          // NOTE: sessionId is NOT persisted here - it uses sessionStorage for tab isolation
        }),
        // CRITICAL FIX: Handle localStorage QuotaExceeded errors
        storage: {
          getItem: (name) => {
            try {
              const item = localStorage.getItem(name)
              return item ? JSON.parse(item) : null
            } catch (error) {
              console.warn('[CartStore] Error reading from localStorage:', error)
              return null
            }
          },
          setItem: (name, value) => {
            try {
              localStorage.setItem(name, JSON.stringify(value))
            } catch (error) {
              if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                console.error('[CartStore] localStorage quota exceeded - clearing old data')
                // Try to clear and retry
                try {
                  localStorage.removeItem(name)
                  localStorage.setItem(name, JSON.stringify(value))
                } catch {
                  console.error('[CartStore] Failed to write to localStorage even after clearing')
                }
              } else {
                console.warn('[CartStore] Error writing to localStorage:', error)
              }
            }
          },
          removeItem: (name) => {
            try {
              localStorage.removeItem(name)
            } catch (error) {
              console.warn('[CartStore] Error removing from localStorage:', error)
            }
          }
        },
        // CRITICAL FIX: Validate reservation expiration on rehydration
        // This handles the case where user closes browser and reopens after reservation expired
        onRehydrateStorage: () => (state, error) => {
          if (error) {
            console.error('Error rehydrating cart store:', error)
            return
          }

          if (state?.reservation?.expiresAt) {
            const now = Date.now()
            if (now >= state.reservation.expiresAt) {
              // Reservation expired while browser was closed
              console.log('[CartStore] Clearing expired reservation on rehydration')
              // Use setTimeout to ensure state is fully hydrated before clearing
              setTimeout(() => {
                useCartStore.getState().clearCart()
              }, 0)
            }
          }
        }
      }
    ),
    {
      name: 'cart-store'
    }
  )
)
